import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import api from 'state/axios';
import { addDays, isWithinInterval } from "date-fns";
import toast from "react-hot-toast";
import {
  useMediaQuery,
  useTheme, 
  Box,
  CircularProgress,
  Typography,
  Tooltip,
  FormControl, 
  MenuItem, 
  InputLabel, 
  Select, 
} from "@mui/material";
import UpdateIcon from "@mui/icons-material/Update";
import SortIcon from '@mui/icons-material/Sort';
import { useDateRangeContext } from "contexts/DateRangeContext";
import { setFlights } from "state";
import { LAYOUT_HEIGHT, EXCLUDED_FLIGHT_STATUS } from "state/constants";
import { fetchFlights } from "state/api";
import { removeItemByValueImmutable,
  filterFlights,
  sortMultipleFields,
} from "state/helpers";
import Header from "components/Header";
import FlexBetween from "components/FlexBetween";
import FlexCenter from "components/FlexCenter";
import StyledButton from 'components/StyledButton';
import FilterButton from 'components/FilterButton';
import ScrollToTop from 'components/ScrollToTop';
import FlightFilterDialog from 'components/FlightFilterDialog';
import FlightList from "./FlightList";
import FlightStepperDialog from "./FlightStepperDialog";


const TATInstances = () => {
  const dispatch = useDispatch();
  const timeMode = useSelector((state) => state.timemode);
  const flightsData = useSelector((state) => state.flights);
  const theme = useTheme();
  const primaryDark = theme.palette.primary.dark;
  const textPrimaryColor = theme.palette.text.primary;
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { startDate, endDate } = useDateRangeContext();

  const scrollRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [filters, setFilters] = useState(null);
  const [sortOption, setSortOption] = useState('none');
  const [port, setPort] = useState('ALL');
  const [openStepper, setOpenStepper] = useState(false);

  const [selectedFlight, setSelectedFlight] = useState(null);
  const [tatTemplates, setTatTemplates] = useState([]);
  const [tatInstances, setTatInstances] = useState([]);
  const [processedFlights, setProcessedFlights] = useState([]);


  const fetchTATTemplates = useCallback(async () => {
    const res = await api.get('/tattemplates/active');
    return res.data;
  }, []);

  const fetchTATInstances = useCallback(async () => {
    if (!startDate || !endDate) return []; 

    const payload = { startDate: addDays(startDate, -1), endDate: addDays(endDate, 1)}
    const res = await api.post('/tatinstances', payload);
    return res.data;
  }, [startDate, endDate]);
  
  const fetchData = useCallback(async () => {
    try {      
      setError(null);     
      setLoading(true);
      
      const [
        tatTemplatesData,
        tatInstancesData,
      ] = await Promise.all([
        fetchTATTemplates(),
        fetchTATInstances(),
      ]);

      setTatTemplates(tatTemplatesData || []);        
      setTatInstances(tatInstancesData || []);      
    } catch (error) {
      setError(error);
      toast.error(error?.message || "Failed to fetch data!");
    } finally {
      setLoading(false);
    } 
  }, [fetchTATTemplates, fetchTATInstances]);
      
  useEffect(() => {    
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const processFlights = async () => {
      try {
        setError(null);
        setLoading(true);
       
        const imutated = removeItemByValueImmutable(
          flightsData,
          "flightStatus",
          EXCLUDED_FLIGHT_STATUS
        );
        const filtered = filterFlights({ flights: imutated, filters, timeMode });
        const sorted = sortMultipleFields(filtered, [
          { field: "fullEtd", direction: "asc" },
          { field: "flightReg", direction: "asc" },
        ]);

        const enriched = sorted.map((f) => {
          const matchedInstance = tatInstances?.find((i) => {
            const sameDate = new Date(i?.flightDate).getTime() === new Date(f?.flightDate).getTime();
            return (
              sameDate &&
              i.flightDesc === f.flightDesc &&
              i.dep === f.dep
            );
          });

          if (matchedInstance) {
            const total = matchedInstance.tasks.length;
            const done = matchedInstance.tasks.filter(t => t.status === "completed").length;
            const progress = total ? Math.round((done / total) * 100) : 0;
            
            return {
              ...f,
              tasks: matchedInstance.tasks || [],
              templateId: matchedInstance.templateId || null,
              instanceId: matchedInstance._id,
              isCompleted: matchedInstance.isCompleted,
              progress,
            };
          } else {
            // Hàm chấm điểm template dựa trên mức độ match
            const scoreTemplate = (t) => {
              let score = 0;

              if (!t.acTypeCodes?.length || t.acTypeCodes.includes(f.acType)) score++;
              if (!t.acRegs?.length || t.acRegs.includes(f.acReg)) score++;
              if (!t.airportCodes?.length || t.airportCodes.includes(f.dep)) score++;
              if (!t.sectors?.length || t.sectors.includes(f.sector)) score++;

              return score;
            };

            // Chọn template có điểm cao nhất
            const bestTemplate = tatTemplates?.reduce((best, t) => {
              const score = scoreTemplate(t);
              const bestScore = best ? scoreTemplate(best) : -1;
              return score > bestScore ? t : best;
            }, null);

            // Gán tasks từ template đã chọn
            const tasks = bestTemplate?.tasks?.map((t, i) => (
              {
                taskCode: t.taskCode,
                taskName: t.taskName,
                preceding: t.preceding,
                startTime: t.startTime || 0,
                endTime: t.endTime || 0,
                duration: t.duration || 0,
                priority: t.priority || 0,
                actualStartDate: null,
                actualEndDate: null,
                actualDuration: null,
                remarks: "",
                status: "pending",
                assignedRole: "Unassigned",
              }
            )) || [];
            
            return { 
              ...f, 
              tasks, 
              templateId: bestTemplate?._id || null,
              progress: 0,
            };
          }
        });

        setProcessedFlights(enriched);
      } catch (error) {
        setError(error);
        toast.error(error?.message || "Failed to load flights!");
      } finally {
        setLoading(false);
      }
    };

    processFlights();
  }, [
    timeMode, 
    flightsData, 
    filters, 
    tatInstances, 
    tatTemplates
  ]);

  const sortedFlights = useMemo(() => {
    if (sortOption === 'none') return processedFlights;

    const sorted = [...processedFlights];
    sorted.sort((a, b) => {
        const aEtd = a.fullEtd ? new Date(a.fullEtd) : null;
        const bEtd = b.fullEtd ? new Date(b.fullEtd) : null;
        const aReg = a.flightReg || '';
        const bReg = b.flightReg || '';

        // sortOption === 'flightReg_fullEtd')                
        if (aReg !== bReg) return aReg.localeCompare(bReg);
        if (!aEtd && !bEtd) return 0;
        if (!aEtd) return 1;
        if (!bEtd) return -1;
        return aEtd - bEtd;
    });

    return sorted;
  }, [processedFlights, sortOption]);

  const enrichedFlights = useMemo(() => {
    if (port === 'ALL') return sortedFlights;
    return sortedFlights?.filter(f => f.dep === port);
  }, [sortedFlights, port]);

  
  const handleFlights = useCallback(async () => {
    if (!startDate || !endDate) return null; 
        
    fetchData();

    try {
      setError(null);
      setLoading(true);
      
      const fetchedFlights = await fetchFlights(addDays(startDate, -1), addDays(endDate, 1));      
      if (fetchedFlights) {        
        const filteredFlights = fetchedFlights.filter(flight => {
          const flightDateToCompare =
          timeMode === 'UTC' ? flight.flightDate : flight.flightDateLocalBase;
      
          return isWithinInterval(flightDateToCompare, { start: startDate, end: endDate });
        });
        
        dispatch(setFlights({ flights: filteredFlights }));
      }
      toast.success("Refresh flights successfully.");
    } catch (error) {
      setError(error);
      toast.error(error?.message || "Failed to fetch flights!");
    } finally {
      setLoading(false);
    }
  }, [
    dispatch, 
    timeMode, 
    startDate, 
    endDate, 
    fetchData,
  ]);

  const handleOpenStepper = (flight) => {
    setSelectedFlight(flight);
    setOpenStepper(true);
  };

  const handleCloseStepper = () => {
    setSelectedFlight(null);
    setOpenStepper(false);
  };

  const handleFilterOpen = () => setIsFilterDialogOpen(true);
  const handleFilterClose = () => setIsFilterDialogOpen(false);  
  const handleFilterApply = (filterValues) => {
    setFilters(filterValues); 
  };

  const handleSortToggle = () => {
    setSortOption((prev) => {
      if (prev === 'none') return 'flightReg_fullEtd';
      if (prev === 'flightReg_fullEtd') return 'none';
      return 'none';
    });
  };
  
  const acTypeOptions = Array.isArray(processedFlights) ? [...new Set(processedFlights.map(flight => flight.acType))].sort() : [];
  const flightRegOptions = Array.isArray(processedFlights) ? [...new Set(processedFlights.map(flight => flight.flightReg))].sort() : [];
  const sectorTypeOptions = Array.isArray(processedFlights) ? [...new Set(processedFlights.map(flight => flight.sectorType))].sort() : [];
  const departureTypeOptions = Array.isArray(processedFlights) ? [...new Set(processedFlights.map(flight => flight.departureType))].sort() : [];
  const arrivalTypeOptions = Array.isArray(processedFlights) ? [...new Set(processedFlights.map(flight => flight.arrivalType))].sort() : [];
  const airportOptions = useMemo(() => {
    if (!Array.isArray(processedFlights)) return [];
  
    const depPorts = processedFlights.map(flight => flight.dep);
    const arrPorts = processedFlights.map(flight => flight.arr);
    const combinedPorts = [...depPorts, ...arrPorts];
    
    return Array.from(new Set(combinedPorts)).sort(); 
  }, [processedFlights]);


  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header 
          title="TAT Instances" 
          subtitle={`${loading ? 'Loading...' : 'Tracking Board'}`}
        />
        {!loading && (                    
          <Box display="flex" alignItems="center" gap={1}>
            <FormControl size="small">
              <InputLabel>Port</InputLabel>
              <Select
                label="Port"
                value={port ? port : ''}
                onChange={(e) => setPort(e.target.value)}
                sx={{ 
                  width: isMobile ? '5rem' : '6rem',
                }}
              >
                <MenuItem key='ALL' value='ALL'>ALL</MenuItem>
                {airportOptions?.map((ap, index) => (
                  <MenuItem key={index} value={ap}>
                    {ap}
                  </MenuItem>
                ))}                                                               
              </Select>
            </FormControl>

            <Tooltip title={`Sort by ${sortOption === 'flightReg_fullEtd' ? 'Reg + Etd' : 'Etd'}`} arrow>
              <span>
                <StyledButton 
                  onClick={handleSortToggle}
                  sx={{
                    color: sortOption === 'none' 
                      ? `${textPrimaryColor} !important` 
                      : `${primaryDark} !important`,
                  }}
                >
                  <SortIcon />
                </StyledButton>
              </span> 
            </Tooltip>

            {!isMobile && (
              <Tooltip title="Refresh" arrow>
                <span>
                  <StyledButton onClick={handleFlights}>
                    <UpdateIcon />
                  </StyledButton>
                </span>              
              </Tooltip>
            )}
            <FilterButton
              filters={filters}
              onClick={handleFilterOpen}
            />
          </Box>
        )}  
      </FlexBetween>
      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <CircularProgress />
        </FlexCenter>
      ) : error ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <Typography color="error">Error: {error?.message || ''}</Typography>
        </FlexCenter>        
      ) : (        
        <Box
          ref={scrollRef}
          height={LAYOUT_HEIGHT}  
          sx={{ overflowY: 'auto' }}    
        >                            
          <FlightList
            flights={enrichedFlights}
            selectedFlight={selectedFlight}
            onSelect={handleOpenStepper}
          />  

          <ScrollToTop scrollRef={scrollRef} />                              
        </Box>        
      )}

      <FlightStepperDialog
        open={openStepper}
        onClose={handleCloseStepper}
        flight={selectedFlight}
        onProgressUpdate={(instanceId, progress, flightInfo) => {
          setProcessedFlights(prev =>
            prev.map(f => {
              if (!flightInfo) {
                const sameFlight =
                  f.flightDate &&
                  selectedFlight &&
                  new Date(f.flightDate).getTime() === new Date(selectedFlight.flightDate).getTime() &&
                  f.flightDesc === selectedFlight.flightDesc &&
                  f.dep === selectedFlight.dep;
               
                if (sameFlight) {
                  return { ...f, progress: 0, instanceId: null };
                }
                return f;
              }
              
              const sameFlight =
                f.flightDate &&
                new Date(f.flightDate).getTime() === new Date(flightInfo.flightDate).getTime() &&
                f.flightDesc === flightInfo.flightDesc &&
                f.dep === flightInfo.dep;

              if (f.instanceId === instanceId || sameFlight) {
                return { ...f, instanceId, progress };
              }

              return f;
            })
          );
        }}
      />

      {isFilterDialogOpen &&
        <FlightFilterDialog
          open={isFilterDialogOpen}
          onClose={handleFilterClose}
          onApply={handleFilterApply}
          acTypeOptions={acTypeOptions} 
          flightRegOptions={flightRegOptions}
          sectorTypeOptions={sectorTypeOptions}
          departureTypeOptions={departureTypeOptions}
          arrivalTypeOptions={arrivalTypeOptions}
        />
      }
    </Box>
  );
};

export default TATInstances;
